import React, { useRef, useEffect } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkIcon from "@mui/icons-material/Bookmark";

const QuestionNavigator = ({
  open,
  onClose,
  currentIndex,
  answers,
  marked,
  onJump,
  isReviewMode = false, // completed/review mode
  correctness = null,   // array aligned with answers: true/false/null
}) => {
  const ref = useRef(null);
  const totalQuestions = answers.length;

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 96,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 100,
      }}
    >
      <Box
        ref={ref}
        sx={{
          width: 440,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          p: 3,
          position: "relative",
        }}
      >
        {/* Pointer */}
        <Box
          sx={{
            position: "absolute",
            bottom: -24,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "24px solid transparent",
            borderRight: "24px solid transparent",
            borderTop: "24px solid #fff",
          }}
        />

        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight={600} fontSize={22} textAlign="center" mb={2}>
          Core Algebra Skills
        </Typography>

        <Box sx={{ height: 1.5, backgroundColor: "#d1d5db", mb: 0.5 }} />

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            py: 1,
            fontSize: 13,
          }}
        >
          <LegendItem label="Current" type="current" />
          <LegendItem label="Unanswered" type="unanswered" />
          <LegendItem label="For Review" type="review" />
          {isReviewMode && <LegendItem label="Incorrect" type="incorrect" />}
        </Box>

        <Box sx={{ height: 1.5, backgroundColor: "#d1d5db", mb: 2 }} />

        {/* Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gap: 1.75,
            justifyItems: "center",
            mb: 3,
            mt: 3,
          }}
        >
          {Array.from({ length: totalQuestions }, (_, i) => {
            const isCurrent = i === currentIndex;
            const isAnswered = answers[i] !== null && answers[i] !== undefined;

            const isIncorrect =
              isReviewMode &&
              isAnswered &&
              Array.isArray(correctness) &&
              correctness[i] === false;

            const isCorrect =
              isReviewMode &&
              isAnswered &&
              Array.isArray(correctness) &&
              correctness[i] === true;

            // Existing behavior: answered = blue, unanswered = white dotted
            // New behavior in review mode: incorrect answered = red
            const backgroundColor = isIncorrect
              ? "#dc2626"
              : isCorrect
              ? "#16a34a"
              : isAnswered
              ? "#2563eb"
              : "#fff";

            const color = isIncorrect
              ? "#fff"
              : isAnswered
              ? "#fff"
              : "#111827";

            const border = isAnswered ? "none" : "2px dotted #9ca3af";

            return (
              <Box
                key={i}
                onClick={() => onJump(i)}
                sx={{
                  width: 26,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: "pointer",
                  backgroundColor,
                  color,
                  border,
                  position: "relative",
                }}
              >
                {isCurrent && (
                  <LocationOnOutlinedIcon
                    sx={{
                      position: "absolute",
                      top: -16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: 18,
                      color: "#111827",
                    }}
                  />
                )}

                {isCurrent ? <u>{i + 1}</u> : i + 1}

                {marked?.[i] && (
                  <BookmarkIcon
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      fontSize: 14,
                      color: "#dc2626",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="outlined"
            sx={{
              borderRadius: "25px",
              px: 3,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Go to Review Page
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const LegendItem = ({ label, type }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {type === "current" && <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
    {type === "unanswered" && ( <Box sx={{ width: 12, height: 12, border: "2px dotted #9ca3af" }} />)}
    {type === "review" && <BookmarkIcon sx={{ fontSize: 18, color: "#dc2626" }} />}
    {type === "incorrect" && ( <Box sx={{ width: 12, height: 12, backgroundColor: "#dc2626" }} />)}
    <span>{label}</span>
  </Box>
);

export default QuestionNavigator;
